import { useState } from "react";
import { Mail, Check, AlertCircle, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function NewsletterForm({ compact = false, source = "landing", testIdPrefix = "newsletter" }) {
  const [email, setEmail] = useState("");
  const [journals, setJournals] = useState([]);
  const [status, setStatus] = useState("idle"); // idle|loading|success|already|error
  const [errorMsg, setErrorMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("loading");
    setErrorMsg("");
    try {
      const r = await fetch(`${API}/api/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), journals, source }),
      });
      const data = await r.json();
      if (!r.ok) {
        setStatus("error");
        setErrorMsg(data.detail || "Subscription failed");
        return;
      }
      setStatus(data.status === "already_subscribed" ? "already" : "success");
    } catch (err) {
      setStatus("error");
      setErrorMsg(String(err));
    }
  };

  const toggleJournal = (j) => {
    setJournals((prev) =>
      prev.includes(j) ? prev.filter((x) => x !== j) : [...prev, j]
    );
  };

  return (
    <form
      onSubmit={submit}
      data-testid={`${testIdPrefix}-form`}
      className={`flex flex-col gap-3 ${compact ? "" : "max-w-md"}`}
    >
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Mail
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--sci-text-muted)] pointer-events-none"
          />
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={status === "loading"}
            placeholder="your@institution.edu"
            data-testid={`${testIdPrefix}-email`}
            aria-label="Email address"
            className={`w-full pl-9 pr-3 py-2.5 text-sm border ${
              compact
                ? "bg-white/5 text-white border-white/20 placeholder:text-white/40"
                : "bg-white text-[var(--sci-navy)] border-[var(--sci-border)] placeholder:text-[var(--sci-text-muted)]"
            } focus:border-[var(--sci-teal)] outline-none transition-colors`}
          />
        </div>
        <button
          type="submit"
          disabled={status === "loading"}
          data-testid={`${testIdPrefix}-submit`}
          className={`${
            compact ? "btn-on-dark" : "btn-primary"
          } px-5 py-2.5 text-xs tracking-wide inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed`}
        >
          {status === "loading" ? (
            <>
              <Loader2 size={13} className="animate-spin" />
              Subscribing
            </>
          ) : (
            "Subscribe"
          )}
        </button>
      </div>

      {!compact && (
        <div
          className="flex flex-wrap gap-2 text-[11px] font-mono-sci tracking-wider"
          data-testid={`${testIdPrefix}-journal-toggles`}
        >
          {[
            { id: "ijmrcr", label: "IJMRCR" },
            { id: "ijsm", label: "IJSM" },
          ].map((j) => (
            <button
              key={j.id}
              type="button"
              data-testid={`${testIdPrefix}-toggle-${j.id}`}
              onClick={() => toggleJournal(j.id)}
              className={`px-2.5 py-1.5 border transition-colors ${
                journals.includes(j.id)
                  ? "border-[var(--sci-teal)] bg-[var(--sci-teal)] text-white"
                  : "border-[var(--sci-border)] text-[var(--sci-text-muted)] hover:border-[var(--sci-teal)] hover:text-[var(--sci-teal)]"
              }`}
            >
              {j.label}
            </button>
          ))}
        </div>
      )}

      {status === "success" && (
        <div
          data-testid={`${testIdPrefix}-success`}
          className={`flex items-start gap-2 text-xs ${
            compact ? "text-white/80" : "text-[var(--sci-teal)]"
          }`}
        >
          <Check size={13} className="mt-0.5 shrink-0" />
          <span>
            Subscribed. Welcome to the Sci Ink Press digest — we'll be in touch.
          </span>
        </div>
      )}
      {status === "already" && (
        <div
          data-testid={`${testIdPrefix}-already`}
          className={`flex items-start gap-2 text-xs ${
            compact ? "text-white/70" : "text-[var(--sci-text-muted)]"
          }`}
        >
          <Check size={13} className="mt-0.5 shrink-0" />
          <span>You're already on the list — preferences updated.</span>
        </div>
      )}
      {status === "error" && (
        <div
          data-testid={`${testIdPrefix}-error`}
          className="flex items-start gap-2 text-xs text-red-500"
        >
          <AlertCircle size={13} className="mt-0.5 shrink-0" />
          <span>{errorMsg || "Subscription failed. Please try again."}</span>
        </div>
      )}

      <p
        className={`text-[11px] leading-relaxed ${
          compact ? "text-white/55" : "text-[var(--sci-text-muted)]"
        }`}
      >
        Editorial digest only. No advertising. You may unsubscribe at any time
        — see our{" "}
        <a
          href="/privacy"
          className={
            compact
              ? "text-white/80 underline"
              : "text-[var(--sci-navy)] underline"
          }
        >
          Privacy Policy
        </a>
        .
      </p>
    </form>
  );
}
